// src/__business/components/campaign/promo/_product/promoPage/attract/templates/customization/tabs/PickupAddressModal.js
import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, TextField, FormControl, Select, MenuItem, FormHelperText, InputAdornment, FormControlLabel, Checkbox, Button, IconButton, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import EditLocationAltOutlined from '@mui/icons-material/EditLocationAltOutlined';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { rqx } from '../../../../../../../../../../core/request/API';
import { env } from '../../../../../../../../../../core/Env';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    p: 0,
    display: 'flex',
    flexDirection: 'column',
};

const formBoxStyle = {
    bgcolor: '#f7f7fa',
    borderRadius: 2,
    p: 2,
    mb: 2,
    mt: 1,
    overflowY: 'auto',
    maxHeight: '60vh',
};

const PickupAddressModal = ({
    open,
    mode,
    onClose,
    onSave,
    onEdit,
    onSaveUpdate,
    inputs,
    prodTemplate,
    provinces,
    cities,
    barangay,
    provinceLoading,
    cityLoading,
    barangayLoading,
    handlePickupFieldChange,
    isDefault,
    isEnable,
    handleDefaultChange,
    handleEnableChange,
    onOpenMap,
    selectedAddressDetails,
    isGoogleMapsMode,
    setIsGoogleMapsMode,
    ubr_pickup_address
}) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [addressError, setAddressError] = useState('');
    const [addressErrorDialogOpen, setAddressErrorDialogOpen] = useState(false);
    const [addressChecking, setAddressChecking] = useState(false);
    const [startTime, setStartTime] = useState({ value: null, stat: false, msg: '' });
    const [endTime, setEndTime] = useState({ value: null, stat: false, msg: '' });

    useEffect(() => {
        if (mode === 'add') {
            setStartTime({ value: null, stat: false, msg: '' });
            setEndTime({ value: null, stat: false, msg: '' });
        }
    }, [mode]);

    useEffect(() => {
        if (mode === 'view' || mode === 'edit' || ubr_pickup_address) {
            const pickup = inputs?.pickup_datetime?.value;
            if (pickup && pickup.includes(" - ")) {
                const [start, end] = pickup.split(" - ");
                setStartTime({ value: start, stat: false, msg: '' });
                setEndTime({ value: end, stat: false, msg: '' });
            } else {
                setStartTime({ value: null, stat: false, msg: '' });
                setEndTime({ value: null, stat: false, msg: '' });
            }
        }
    }, [mode, inputs]);

    // Check if we're in Google Maps mode based on having full_address value or selectedAddressDetails
    const isInGoogleMapsMode = isGoogleMapsMode || (inputs?.full_address?.value && inputs.full_address.value.trim() !== '');

    const handleStartTimeChange = (event) => {
        const newStartTime = event.target.value;

        // Only validate if endTime is already selected
        if (endTime.value && !isStartBeforeEnd(newStartTime, endTime.value)) {
            setStartTime({ value: newStartTime, stat: true, msg: "Start time must be earlier than end time" });
            return;
        }

        setStartTime({ value: newStartTime, stat: false, msg: '' });
        setEndTime({ value: endTime.value, stat: false, msg: '' });
        handlePickupFieldChange({ target: { name: 'pickup_datetime', value: `${newStartTime} - ${endTime.value}` } });
    };

    const handleEndTimeChange = (event) => {
        const newEndTime = event.target.value;

        if (startTime.value && !isStartBeforeEnd(startTime.value, newEndTime)) {
            setEndTime({ value: newEndTime, stat: true, msg: "End time must be greater than start time" });
            return;
        }

        setStartTime({ value: startTime.value, stat: false, msg: '' });
        setEndTime({ value: newEndTime, stat: false, msg: '' });
        handlePickupFieldChange({ target: { name: 'pickup_datetime', value: `${startTime.value} - ${newEndTime}` } });
    };

    const isStartBeforeEnd = (start, end) => {
        const [startHour, startMinute] = start.split(':').map(Number);
        const [endHour, endMinute] = end.split(':').map(Number);

        const startDate = new Date();
        startDate.setHours(startHour, startMinute, 0, 0);

        const endDate = new Date();
        endDate.setHours(endHour, endMinute, 0, 0);

        return startDate < endDate;
    };

    const handleSetManually = () => {
        // Clear full address when switching to manual mode
        prodTemplate.set({
            ...prodTemplate.data,
            inputs: {
                ...prodTemplate.data.inputs,
                full_address: { value: '', stat: false, msg: '' },
                pickup_place_id: { value: '', stat: false, msg: '' }
            }
        })
        setIsGoogleMapsMode(false);
    };

    const isFormValid = () => {
        let requiredFields = [
            'pickup_branch_code',
            'pickup_branch_name',
            'pickup_num'
        ];

        if (isInGoogleMapsMode) {
            const extrafields = [
                'full_address'
            ]
            requiredFields = [...requiredFields, ...extrafields];
        } else {
            const extrafields = [
                'street_name',
                'area_name',
                'pickup_province',
                'pickup_city',
                'pickup_barangay'
            ];
            requiredFields = [...requiredFields, ...extrafields];
        }

        const areFieldsValid = requiredFields.every(
            (field) =>
                inputs[field] &&
                inputs[field].value &&
                !inputs[field].stat
        );

        // Validate pickup_datetime format: "HH:mm - HH:mm"
        const pickupDatetime = inputs?.pickup_datetime;
        const datetimeStr = pickupDatetime?.value || '';
        const isDatetimeFormatValid = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]\s*-\s*([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(datetimeStr);
        const isPickupDatetimeValid = pickupDatetime && isDatetimeFormatValid && !pickupDatetime.stat && !startTime.stat && !endTime.stat;

        return areFieldsValid && isPickupDatetimeValid;
    };

    const validateAddressOnMap = async () => {
        let address = '';
        let pickupPlaceId = '';

        if (isInGoogleMapsMode || inputs.full_address?.value) {
            console.log('Google Maps mode - using selected place_id');
            address = inputs.full_address.value;
            pickupPlaceId = inputs.pickup_place_id?.value;
        } else {
            address = [
                inputs.street_name.value,
                inputs.area_name.value,
                provinces.find(p => p.id === inputs.pickup_province.value)?.name,
                cities.find(c => c.id === inputs.pickup_city.value)?.name,
                barangay.find(b => b.id === inputs.pickup_barangay.value)?.name,
                inputs.nearest.value,
                'Philippines'
            ].filter(Boolean).join(', ');
            console.log('Manual address:', address);

            const placeData = await rqx.post(`<<tool>>/${env()}/template/google_geocode_address?address=${address}`);
            if (placeData.results && placeData.results.length > 0) {
                pickupPlaceId = placeData.results[0].place_id;
                console.log("placeId: ", pickupPlaceId);
                handlePickupFieldChange({ target: { name: 'pickup_place_id', value: pickupPlaceId } });
            }
        }

        if (address && pickupPlaceId) {
            return true
        } else {
            return false
        }
    };

    // const validateAddressOnMap = async () => {
    //     let address = '';
    //     if (isInGoogleMapsMode && inputs?.full_address?.value) {
    //         address = inputs.full_address.value;
    //     } else {
    //         address = [
    //             inputs.street_name.value,
    //             inputs.area_name.value,
    //             provinces.find(p => p.id === inputs.pickup_province.value)?.name,
    //             cities.find(c => c.id === inputs.pickup_city.value)?.name,
    //             barangay.find(b => b.id === inputs.pickup_barangay.value)?.name,
    //             inputs.nearest.value,
    //             'Philippines'
    //         ].filter(Boolean).join(', ');

    //     }

    //     console.log("address: ", address);

    //     // const url = `https://maps.googleapis.com/maps/api/distancematrix/json?origins=${encodeURIComponent(address)}&destinations=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}`;

    //     try {
    //         const data = await rqx.post(`<<tool>>/${env()}/template/google_distance_matrix?origins=${address}&destinations=${address}`)
    //         if (
    //             data.status === 'OK' &&
    //             data.rows[0].elements[0].status === 'OK'
    //         ) {
    //             if (data.origin_addresses && data.origin_addresses.length > 0) {
    //                 console.log('Full formatted address from Google API:', data.origin_addresses[0]);

    //                 // Get place_id for manual addresses
    //                 if (!isInGoogleMapsMode) {
    //                     const placeData = await rqx.post(`<<tool>>/${env()}/template/google_geocode_address?address=${address}`);
    //                     if (placeData.results && placeData.results.length > 0) {
    //                         const placeId = placeData.results[0].place_id;
    //                         console.log("placeId: ", placeId);
    //                         handlePickupFieldChange({ target: { name: 'pickup_place_id', value: placeId } });
    //                     }
    //                 }
    //             }
    //             return true;
    //         } else {
    //             return false;
    //         }
    //     } catch (err) {
    //         return false;
    //     }
    // };

    const handleSaveClick = async () => {
        setAddressError('');
        setAddressChecking(true);
        const isValid = await validateAddressOnMap();
        setAddressChecking(false);
        if (!isValid) {
            setAddressError('Address not found on the map. Please check your input.');
            setAddressErrorDialogOpen(true);
            return;
        }
        setPendingAction('add');
        setConfirmOpen(true);
    };
    
    const handleUpdateClick = async () => {
        setAddressError('');
        setAddressChecking(true);
        const isValid = await validateAddressOnMap();
        setAddressChecking(false);
        if (!isValid) {
            setAddressError('Address not found on the map. Please check your input.');
            setAddressErrorDialogOpen(true);
            return;
        }
        setPendingAction('edit');
        setConfirmOpen(true);
    };

    const handleConfirm = async () => {
        setConfirmOpen(false);
        if (pendingAction === 'add') {
            onSave();
        } else if (pendingAction === 'edit') {
            onSaveUpdate();
        }
    };
    
    useEffect(() => {
        Object.keys(inputs).forEach(field => {
            if (inputs[field].value && inputs[field].stat) {
                handlePickupFieldChange({ target: { name: field, value: inputs[field].value } });
            }
        });
    }, [inputs]);

    const handleModalClose = () => {
        setIsGoogleMapsMode(false);
        onClose();
    };

    useEffect(() => {
        const runAsync = async () => {
            if (ubr_pickup_address)  {
                setAddressError('');
                setAddressChecking(true);
                const isValid = await validateAddressOnMap();
                setAddressChecking(false);
                if (!isValid) {
                    setAddressError('Address not found on the map. Please check your input.');
                    setAddressErrorDialogOpen(true);
                    return;
                }
                await onSave();
            }
        }

        runAsync();
    }, []);

    useEffect(() => {
        if (!open) {
            setIsGoogleMapsMode(false);
        }
    }, [open, setIsGoogleMapsMode]);


    return (
        <Modal open={open} onClose={handleModalClose}>
            <Box sx={style}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>
                        {mode === 'add' ? 'Add Pickup Address' : mode === 'view' ? 'View Pickup Address' : 'Update Pickup Address'}
                    </Typography>
                    <Box display="flex" gap={1}>
                        {mode === 'view' && (
                            <IconButton onClick={onEdit} size="small">
                                <ModeEditIcon />
                            </IconButton>
                        )}
                        <IconButton onClick={handleModalClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                <Divider />
                <Box sx={{ p: 2, pt: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={formBoxStyle}>

                        <Box display="flex" fontWeight={500} fontSize={15} mb={0.5}>Pickup Warehouse Code <Box color="#FA3E3E">*</Box></Box>
                        <TextField size="small" name="pickup_branch_code" onChange={handlePickupFieldChange}
                            value={inputs?.pickup_branch_code?.value} error={inputs?.pickup_branch_code?.stat} helperText={inputs?.pickup_branch_code?.msg}
                            multiline inputProps={{ maxLength: 200 }} disabled={mode === 'view'}
                            fullWidth sx={{ mb: 1 }}
                        />

                        <Box display="flex" fontWeight={500} fontSize={15} mb={0.5}>Pickup Warehouse Name <Box color="#FA3E3E">*</Box></Box>
                        <TextField size="small" name="pickup_branch_name" onChange={handlePickupFieldChange}
                            value={inputs?.pickup_branch_name?.value} error={inputs?.pickup_branch_name?.stat} helperText={inputs?.pickup_branch_name?.msg}
                            multiline inputProps={{ maxLength: 200 }} disabled={mode === 'view'}
                            fullWidth sx={{ mb: 1 }}
                        />

                        {/* Address Section with Toggle */}
                        {isInGoogleMapsMode ? (
                            /* Show full address field when in Google Maps mode */
                            <>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box display="flex" fontWeight={500} fontSize={15} mb={0.5} pt={1.2}>Full Address <Box color="#FA3E3E">*</Box></Box>
                                    {mode !== 'view' && (
                                        <Box display="flex" gap={1}>
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                sx={{ py: 0.4, fontWeight: 500, fontSize: 10, borderRadius: 2, height: 32 }}
                                                onClick={handleSetManually}
                                            >
                                                Set Manually
                                                <EditLocationAltOutlined sx={{ ml: 1 }} />
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                sx={{ py: 0.4, fontWeight: 500, fontSize: 10, borderRadius: 2, height: 32 }}
                                                onClick={onOpenMap}
                                            >
                                                Update Through Google Map
                                                <LocationOnOutlined sx={{ ml: 1 }} />
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                                <TextField size="small" name="full_address" onChange={handlePickupFieldChange}
                                    value={inputs?.full_address?.value} error={inputs?.full_address?.stat} helperText={inputs?.full_address?.msg}
                                    multiline inputProps={{ maxLength: 500 }} disabled={mode === 'view'}
                                    fullWidth sx={{ my: 1 }}
                                />
                            </>
                        ) : (
                            /* Show individual fields for manual entry */
                            <>
                                <Box display="flex" justifyContent="space-between">
                                    <Box display="flex" fontWeight={500} fontSize={15} mb={0.5} pt={1.2}>Pickup Street Details<Box color="#FA3E3E">*</Box></Box>
                                    {mode !== 'view' && (
                                        <Box display="flex" justifyContent="flex-end" mb={1}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                sx={{ py: 0.4, fontWeight: 500, fontSize: 10, borderRadius: 2, height: 32 }}
                                                onClick={onOpenMap}
                                            >
                                                Set Through Google Map
                                                <LocationOnOutlined sx={{ ml: 1 }} />
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                                <TextField size="small" name="street_name" onChange={handlePickupFieldChange}
                                    value={inputs?.street_name?.value} error={inputs?.street_name?.stat} helperText={inputs?.street_name?.msg}
                                    multiline inputProps={{ maxLength: 200 }} disabled={mode === 'view'}
                                    fullWidth sx={{ mb: 1 }}
                                />
                                <Box display="flex" fontWeight={500} fontSize={15} mb={0.5}>Pickup Area Details<Box color="#FA3E3E">*</Box></Box>
                                <TextField size="small" name="area_name" onChange={handlePickupFieldChange}
                                    value={inputs?.area_name?.value} error={inputs?.area_name?.stat} helperText={inputs?.area_name?.msg}
                                    multiline inputProps={{ maxLength: 200 }} disabled={mode === 'view'}
                                    fullWidth sx={{ mb: 1 }}
                                />
                                <Box display="flex" fontWeight={500} fontSize={15} mb={0.5}>Pickup Region <Box color="#FA3E3E">*</Box></Box>
                                <FormControl size="small" fullWidth error={inputs?.pickup_province?.stat} sx={{ mb: 1 }}>
                                    <Select
                                        name="pickup_province"
                                        value={inputs?.pickup_province?.value}
                                        onChange={handlePickupFieldChange}
                                        error={inputs?.pickup_province?.stat}
                                        disabled={provinceLoading || mode === 'view'}
                                    >
                                        {provinces.map((prov) => (
                                            <MenuItem value={prov.id} key={prov.id}>{prov.name}</MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>{inputs?.pickup_province?.msg}</FormHelperText>
                                </FormControl>
                                <Box display="flex" fontWeight={500} fontSize={15} mb={0.5}>Pickup City <Box color="#FA3E3E">*</Box></Box>
                                <FormControl size="small" fullWidth error={inputs?.pickup_city?.stat} sx={{ mb: 1 }}>
                                    <Select
                                        name="pickup_city"
                                        value={inputs?.pickup_city?.value}
                                        onChange={handlePickupFieldChange}
                                        error={inputs?.pickup_city?.stat}
                                        disabled={cityLoading || !inputs?.pickup_province?.value || mode === 'view'}
                                    >
                                        {cities?.map((city) => (
                                            <MenuItem value={city.id} key={city.id}>{city.name}</MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>{inputs?.pickup_city?.msg}</FormHelperText>
                                </FormControl>
                                <Box display="flex" fontWeight={500} fontSize={15} mb={0.5}>Pickup Barangay <Box color="#FA3E3E">*</Box></Box>
                                <FormControl size="small" fullWidth error={inputs?.pickup_barangay?.stat} sx={{ mb: 1 }}>
                                    <Select
                                        name="pickup_barangay"
                                        value={inputs?.pickup_barangay?.value || ''}
                                        onChange={handlePickupFieldChange}
                                        error={inputs?.pickup_barangay?.stat}
                                        disabled={barangayLoading || !inputs?.pickup_city?.value || mode === 'view'}
                                    >
                                        {barangay?.map((barangay) => (
                                            <MenuItem value={barangay.id} key={barangay.id}>{barangay.name}</MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>{inputs?.pickup_barangay?.msg}</FormHelperText>
                                </FormControl>
                            </>
                        )}

                        <Box display="flex" fontWeight={500} fontSize={15} mb={0.5}>Pickup Contact No. <Box color="#FA3E3E">*</Box></Box>
                        <TextField size="small" name="pickup_num" onChange={handlePickupFieldChange}
                            value={inputs?.pickup_num?.value} error={inputs?.pickup_num?.stat} helperText={inputs?.pickup_num?.msg}
                            onKeyPress={e => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }} inputProps={{ maxLength: 10 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        +63
                                    </InputAdornment>
                                )
                            }}
                            disabled={mode === 'view'}
                            fullWidth sx={{ mb: 1 }}
                        />
                        <Box display="flex" fontWeight={500} fontSize={15} mb={0.5}>Nearest Landmark</Box>
                        <TextField size="small" name="nearest" onChange={handlePickupFieldChange}
                            value={inputs?.nearest?.value}
                            disabled={mode === 'view'}
                            fullWidth sx={{ mb: 1 }}
                        />
                        <Box display="flex" fontWeight={500} fontSize={15} mb={0.5}>Pickup Time <Box color="#FA3E3E">*</Box></Box>
                        <Box display="flex" gap={2} mt={1.5} mb={1}>
                            <TextField type="time" size="small" fullWidth
                                label="Start Time"
                                onChange={handleStartTimeChange}
                                disabled={mode === 'view'}
                                value={startTime?.value || ''}
                                error={inputs?.pickup_datetime?.stat || startTime?.stat}
                                helperText={inputs?.pickup_datetime?.msg || startTime?.msg}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField type="time" size="small" fullWidth
                                label="End Time"
                                onChange={handleEndTimeChange}
                                value={endTime?.value || ''}
                                error={inputs?.pickup_datetime?.stat || endTime?.stat}
                                helperText={endTime?.msg}
                                disabled={mode === 'view'}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                        <Box display="flex" gap={2} mb={1} >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isDefault}
                                        onChange={handleDefaultChange}
                                        color="primary"
                                        disabled={mode === 'view'}
                                    />
                                }
                                label="Default Address"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isEnable}
                                        onChange={handleEnableChange}
                                        color="primary"
                                        disabled={mode === 'view'}
                                    />
                                }
                                label="Enable"
                            />
                        </Box>
                    </Box>
                    {/* Modal actions */}
                    {mode === 'add' && (
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{ mt: 2, py: 1.2, fontWeight: 600, fontSize: 16, borderRadius: 2 }}
                            onClick={handleSaveClick}
                            disabled={!isFormValid() || addressChecking}
                            loading={`${addressChecking}`}
                        >
                            {addressChecking ? 'Validating Address...' : 'Save Pickup Address'}
                        </Button>
                    )}
                    {mode === 'edit' && (
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{ mt: 2, py: 1.2, fontWeight: 600, fontSize: 16, borderRadius: 2 }}
                            onClick={handleUpdateClick}
                            disabled={!isFormValid() || addressChecking}
                            loading={`${addressChecking}`}
                        >
                            {addressChecking ? 'Validating Address...' : 'Save Changes'}
                        </Button>
                    )}
                </Box>
                <Dialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            boxShadow: 8,
                            minWidth: 340,
                            p: 2
                        }
                    }}
                >
                    <DialogContent sx={{ p: 3, textAlign: 'center', fontSize: 18, fontWeight: 500 }}>
                        Save this pickup address?
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'center', gap: 2, pb: 2 }}>
                        <Button onClick={() => setConfirmOpen(false)} variant="outlined" color="inherit" sx={{ borderRadius: 2, px: 4 }}>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirm} color="primary" variant="contained" sx={{ borderRadius: 2, px: 4 }}>
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={addressErrorDialogOpen}
                    onClose={() => setAddressErrorDialogOpen(false)}
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            boxShadow: 8,
                            minWidth: 340,
                            p: 2
                        }
                    }}
                >
                    <DialogContent sx={{ pt: 1, pb: 2 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
                            {addressError}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
                        <Button onClick={() => setAddressErrorDialogOpen(false)} variant="contained" autoFocus sx={{ borderRadius: 2, px: 4 }}>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Modal>
    );
};

export default PickupAddressModal;